import React, { useState } from 'react'

import { KeyRoundIcon, MessageSquareIcon, RefreshCwIcon } from 'lucide-react'

import { Divider, Stack, Typography } from '@mui/material'

import { Button, Skeleton, Alert } from '@repo/ui'
import { ConfirmSettingModal, InfoTooltip } from 'components'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'

import {
  useAuthFactorsQuery,
  useRegenerateRecoveryCodesMutation,
  useSetPreferredAuthFactorMutation,
  useStartTotpEnrollmentMutation,
  type AuthFactor,
  type StartTotpEnrollmentResponse,
} from 'hooks/api/useAuthFactors'

import { AuthFactorRow } from './AuthFactorRow'
import { authenticatorPrompt } from './authFactorStatus'
import { EnrollTotpModal } from './EnrollTotpModal'
import { EnrollSmsModal } from './EnrollSmsModal'
import { RemoveFactorModal } from './RemoveFactorModal'
import { RecoveryCodesModal } from './RecoveryCodesModal'
import { StepUpModal } from './StepUpModal'
import { isStepUpRequired, needsSignInAgain } from 'hooks/api/useAuthFactors'

interface TwoFactorSectionProps {
  /** D1/D3 — a `platform_admin` with no verified factor. Mandatory, no skip. */
  isEnrollmentRequired: boolean
}

/**
 * VRG-490 — enrolled-factors list + enroll/remove/recovery-codes flows.
 * Shared shape between `SecurityPage` (apps/platform) — this file — and its
 * `apps/app` counterpart component, modulo the enrollment-required banner
 * (owner accounts have no privilege tiers, so that prop is always false
 * there).
 */
export const TwoFactorSection = ({ isEnrollmentRequired }: TwoFactorSectionProps) => {
  const { open: notification } = useSnackbar()
  const { factors, allowedMethods, isLoading, refetch } = useAuthFactorsQuery()
  const { setPreferredAuthFactorAsync, isSettingPreferredAuthFactor } = useSetPreferredAuthFactorMutation()

  const [totpEnrollment, setTotpEnrollment] = useState<StartTotpEnrollmentResponse | null>(null)
  const [smsModalOpen, setSmsModalOpen] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<AuthFactor | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  // §43 — regenerating is a downstream, hard-to-reverse action (every existing
  // code stops working the instant this confirms, including any a person
  // already printed or saved), so it's gated the same way a certificate
  // activation trigger or scan destination is: state the consequence, THEN
  // ask, via the shared `ConfirmSettingModal` — not a bare "Generate codes"
  // button that reads as "show me my codes" (which is exactly how it was
  // reported: it silently invalidated the existing set).
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false)

  const { startTotpEnrollmentAsync, isStartingTotpEnrollment } = useStartTotpEnrollmentMutation()
  const { regenerateRecoveryCodesAsync, isRegeneratingRecoveryCodes } = useRegenerateRecoveryCodesMutation()

  // A factor the role policy no longer allows does not count — it is never challenged and
  // does not satisfy the platform_admin requirement (`allowed` absent = older API = allowed).
  const verifiedFactors = factors.filter(f => f.verified && f.allowed !== false)
  const hasVerifiedFactor = verifiedFactors.length > 0
  const hasTotp = factors.some(f => f.type === 'totp' && f.verified)
  const hasSms = factors.some(f => f.type === 'sms' && f.verified)
  const canAddTotp = !hasTotp && allowedMethods.includes('totp')
  const canAddSms = !hasSms && allowedMethods.includes('sms')
  // "Make primary" only means something with a choice to make.
  const canChoosePreferred = verifiedFactors.length > 1
  // Text messages are the ONLY usable method (stronger when those texts are blocked because
  // the number replied STOP): prompt for an authenticator app before it becomes a lockout.
  const backupPrompt = canAddTotp ? authenticatorPrompt(factors, allowedMethods) : null

  const handleMakePreferred = async (factor: AuthFactor) => {
    try {
      await setPreferredAuthFactorAsync({ factorId: factor.id })
      notification({
        type: 'success',
        // VRG-822 — "primary", matching the button that got the user here. The
        // control said "Use first" and the toast said "used first"; renaming only
        // the button would leave the confirmation describing a different action.
        title: factor.type === 'totp' ? 'Authenticator app is now primary' : 'SMS is now primary',
        text: 'You can still switch methods when you sign in.',
      })
    } catch (error) {
      notification({ type: 'error', title: 'Could not change your sign-in method', text: getErrorMessage(error) })
    }
  }

  // Every change to second factors while one exists — adding another, removing one,
  // regenerating recovery codes — first needs a step-up with an existing factor (the API
  // enforces it and answers STEP_UP_REQUIRED otherwise). The pending action runs once it
  // passes. Without this a stolen access token could remove the owner's factors and enroll
  // its own.
  type PendingAction = { kind: 'totp' } | { kind: 'sms' } | { kind: 'remove'; factor: AuthFactor } | { kind: 'regenerate' }
  const [stepUpFor, setStepUpFor] = useState<PendingAction | null>(null)
  const requireStepUpOr = (action: PendingAction, run: () => void) => {
    if (hasVerifiedFactor) setStepUpFor(action)
    else run()
  }
  const runPending = (action: PendingAction) => {
    if (action.kind === 'totp') handleStartTotp()
    if (action.kind === 'sms') setSmsModalOpen(true)
    if (action.kind === 'remove') setRemoveTarget(action.factor)
    if (action.kind === 'regenerate') setRegenerateConfirmOpen(true)
  }

  const handleStartTotp = async () => {
    try {
      const response = await startTotpEnrollmentAsync()
      setTotpEnrollment(response)
    } catch (error) {
      // The grant may have lapsed (5 minutes) — ask for the step-up again. Unless there is
      // no factor left to step up WITH (one was removed earlier in this session): then the
      // only way forward is a fresh sign-in, and the message says so.
      if (isStepUpRequired(error) && !needsSignInAgain(error)) {
        setStepUpFor({ kind: 'totp' })
        return
      }
      notification({ type: 'error', title: 'Could not start setup', text: getErrorMessage(error) })
    }
  }

  const handleTotpConfirmed = (codes?: string[]) => {
    setTotpEnrollment(null)
    refetch()
    if (codes) setRecoveryCodes(codes)
  }

  const handleSmsConfirmed = (codes?: string[]) => {
    setSmsModalOpen(false)
    refetch()
    if (codes) setRecoveryCodes(codes)
  }

  const handleRegenerateRecoveryCodes = async () => {
    try {
      const response = await regenerateRecoveryCodesAsync()
      setRegenerateConfirmOpen(false)
      setRecoveryCodes(response.recoveryCodes)
    } catch (error) {
      notification({ type: 'error', title: 'Could not generate codes', text: getErrorMessage(error) })
    }
  }

  return (
    <Stack spacing={4}>
      {isEnrollmentRequired && !hasVerifiedFactor && (
        <Alert
          severity="warning"
          title="Two-factor authentication required"
          description="Your role requires two-factor authentication before you can continue using Vera. Add an authenticator app or phone number below."
        />
      )}

      {!isLoading && backupPrompt && (
        <Alert
          severity={backupPrompt.severity}
          title={backupPrompt.title}
          description={backupPrompt.description}
          button={
            <Button
              disableElevation
              variant="secondary"
              startIcon={<KeyRoundIcon strokeWidth={1.5} size={16} />}
              loading={isStartingTotpEnrollment}
              disabled={isStartingTotpEnrollment}
              // Same step-up rule as the "Add authenticator app" button below. The step-up
              // takes a recovery code, which is the way in when texts are blocked.
              onClick={() => requireStepUpOr({ kind: 'totp' }, handleStartTotp)}
            >
              Add authenticator app
            </Button>
          }
        />
      )}

      {isLoading ? (
        <Stack spacing={3}>
          <Skeleton variant="rounded" height={40} width={280} />
          <Skeleton variant="rounded" height={40} width={280} />
        </Stack>
      ) : factors.length > 0 ? (
        // VRG-490 round 2 — spacing={4} (16px), not {2} (8px), between the
        // "Methods set up" caption and the row list. Matches the caption-to-
        // content gap the house already uses ahead of a settings list
        // (`ShortcutsCard`'s "Built-in" caption, `NotificationsPage`'s
        // category captions — both spacing={4} on this app's 4px base).
        <Stack spacing={4}>
          <Typography variant="body4" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Methods set up
          </Typography>
          <Stack divider={<Divider />} spacing={0}>
            {factors.map(factor => (
              <AuthFactorRow
                key={factor.id}
                factor={factor}
                onRemove={(factor) => requireStepUpOr({ kind: 'remove', factor }, () => setRemoveTarget(factor))}
                onMakePreferred={canChoosePreferred ? handleMakePreferred : undefined}
                isChangingPreferred={isSettingPreferredAuthFactor}
              />
            ))}
          </Stack>
        </Stack>
      ) : null}

      {/* working-conventions §46 — already-enrolled is RECORD STATE ("this
          type already has a verified factor"), not a permission block, so the
          resolving affordance (Remove, on the row above) stays reachable and
          the Add button HIDES rather than disabling with no explanation. */}
      {/* VRG-490 — `useFlexGap` (real CSS `gap` on the row), not MUI Stack's
          default margin-based row spacing. Without it, MUI applies spacing via
          `& > :not(style) ~ :not(style) { margin-left }`, a rule keyed on
          SIBLING POSITION — so when "Add authenticator app" unmounts (because
          the 2FA-factors query resolves and the account already has a
          verified TOTP factor), "Add phone" stops matching the selector and
          its own `margin-left` recomputes from 12px to 0. Because the shared
          `Button` (`packages/ui/components/input/Button`) carries a blanket
          `transition: "all 0.2s ease-in"` for its hover/loading states, that
          margin-left CHANGE gets animated too — reading as "Add phone sliding
          left" on load. `gap` lives on the Stack itself, not on the children,
          so a sibling unmounting never changes a REMAINING child's own CSS
          properties — nothing left for Button's transition to catch. */}
      {(canAddTotp || canAddSms) && (
        <Stack direction="row" spacing={3} useFlexGap>
          {canAddTotp && (
            <Button
              disableElevation
              variant="secondary"
              startIcon={<KeyRoundIcon strokeWidth={1.5} size={16} />}
              loading={isStartingTotpEnrollment}
              disabled={isStartingTotpEnrollment}
              onClick={() => requireStepUpOr({ kind: 'totp' }, handleStartTotp)}
            >
              Add authenticator app
            </Button>
          )}
          {canAddSms && (
            <Button
              disableElevation
              variant="secondary"
              startIcon={<MessageSquareIcon strokeWidth={1.5} size={16} />}
              onClick={() => requireStepUpOr({ kind: 'sms' }, () => setSmsModalOpen(true))}
            >
              Add phone
            </Button>
          )}
        </Stack>
      )}

      {hasVerifiedFactor && (
        <>
          <Divider />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center">
                <Typography variant="body2">Recovery codes</Typography>
                {/* Definition comes from the glossary, never a sentence typed here (§44/§59).
                    This term earns a tooltip where the other two 2FA terms do not: the
                    section's own description already explains what two-factor auth and an
                    authenticator app are, whereas the load-bearing facts about recovery
                    codes — shown once, unrecoverable, replaced not re-read — are exactly
                    what people get wrong. An (i) on everything is noise. */}
                <InfoTooltip term="recovery-code" />
              </Stack>
              <Typography variant="body3" color="text.secondary">
                Single-use codes to sign in if you lose access to your other methods. They're shown once and
                can't be retrieved again, only replaced with a new set.
              </Typography>
            </Stack>
            <Button
              disableElevation
              variant="secondary"
              startIcon={<RefreshCwIcon strokeWidth={1.5} size={16} />}
              onClick={() => requireStepUpOr({ kind: 'regenerate' }, () => setRegenerateConfirmOpen(true))}
            >
              Regenerate recovery codes
            </Button>
          </Stack>
        </>
      )}

      <EnrollTotpModal
        open={Boolean(totpEnrollment)}
        onClose={() => setTotpEnrollment(null)}
        enrollment={totpEnrollment}
        onConfirmed={handleTotpConfirmed}
      />
      <StepUpModal
        open={stepUpFor !== null}
        onClose={() => setStepUpFor(null)}
        onVerified={() => {
          const next = stepUpFor
          setStepUpFor(null)
          if (next) runPending(next)
        }}
      />
      <EnrollSmsModal open={smsModalOpen} onClose={() => setSmsModalOpen(false)} onConfirmed={handleSmsConfirmed} />
      <RemoveFactorModal factor={removeTarget} open={Boolean(removeTarget)} onClose={() => setRemoveTarget(null)} />
      <RecoveryCodesModal codes={recoveryCodes} open={Boolean(recoveryCodes)} onClose={() => setRecoveryCodes(null)} />
      <ConfirmSettingModal
        open={regenerateConfirmOpen}
        onClose={() => setRegenerateConfirmOpen(false)}
        onConfirm={handleRegenerateRecoveryCodes}
        title="Regenerate recovery codes?"
        consequence="Your current recovery codes will stop working immediately, including any copy you've already printed or saved. You'll be shown a new set of codes once, and only once — save them somewhere safe before closing that screen."
        confirmLabel="Regenerate recovery codes"
        loading={isRegeneratingRecoveryCodes}
      />
    </Stack>
  )
}
